import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';
import styles from './AdminUserProfile.module.css';
import { COUNTRIES, INDIAN_STATES } from '../../data/locations';

type PreviewComponent = React.FC<{ path: string | null | undefined; label: string }>;

// These option lists are copied from the user's own forms (pages/Kyc/Kyc.tsx and
// pages/Kyb/KybSection.tsx) so the admin sees exactly what the user sees.
type DocCategory = 'identity' | 'address' | 'income';

const DOC_OPTIONS: Record<DocCategory, string[]> = {
  identity: ['Govt ID', 'Tax ID', 'Passport', "Driver's License", 'Others'],
  address: ['Bank Statement', 'Mobile Bill', 'Electricity Bill', 'Internet Bill', 'Gas Book', 'Other Utility Bill'],
  income: ['ITR with Computations', 'Transaction Records', 'Other Financial Documents'],
};

const CATEGORY_LABEL: Record<DocCategory, string> = {
  identity: 'Proof of Identity', address: 'Proof of Address', income: 'Proof of Income',
};

const KYB_DOCS = [
  { key: 'registration_certificate', label: 'Company Registration Certificate' },
  { key: 'gst_pan_proof', label: 'GST / PAN Proof' },
  { key: 'ubo_id_proof', label: "UBO's Government ID Proof" },
];

const FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp';

const dateLabel = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '');

// Says who entered a record: the user himself, or an admin on his behalf.
export const FilledByBadge: React.FC<{ adminName?: string | null; adminId?: string | null; at?: string | null }> = ({ adminName, adminId, at }) => (
  adminId ? (
    <span className={`${styles.byBadge} ${styles.byAdmin}`}>
      Filled by admin{adminName ? `: ${adminName}` : ''}{at ? ` · ${dateLabel(at)}` : ''}
    </span>
  ) : (
    <span className={`${styles.byBadge} ${styles.byUser}`}>Submitted by user</span>
  )
);

const Req = () => <span style={{ color: '#e2534a' }}> *</span>;

// Approve / Reject buttons used for every admin-filled or user-filled record.
const ReviewButtons: React.FC<{ url: string; status: string; onChanged: () => void; what: string }> = ({ url, status, onChanged, what }) => {
  const [busy, setBusy] = useState(false);
  const set = async (next: 'approved' | 'rejected') => {
    setBusy(true);
    try {
      await axiosClient.patch(url, { status: next });
      toast.success(`${what} ${next}.`);
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update the status.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      {status !== 'approved' && <button className={styles.saveBtn} style={{ padding: '4px 12px' }} disabled={busy} onClick={() => set('approved')}>Approve</button>}
      {status !== 'rejected' && <button className={styles.cancelEditBtn} style={{ padding: '4px 12px' }} disabled={busy} onClick={() => set('rejected')}>Reject</button>}
    </span>
  );
};

/* ---------------- Personal details ---------------- */

interface PersonalProps {
  userId: string;
  accountEmail: string;
  personal: any | null;
  onClose: () => void;
  onChanged: () => void;
}

export const AdminPersonalForm: React.FC<PersonalProps> = ({ userId, accountEmail, personal, onClose, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: personal?.title || '', firstName: personal?.first_name || '', lastName: personal?.last_name || '',
    middleName: personal?.middle_name || '', dob: personal?.dob ? String(personal.dob).slice(0, 10) : '',
    gender: personal?.gender || '', maritalStatus: personal?.marital_status || '',
    email: personal?.email || accountEmail || '', address: personal?.address || '', city: personal?.city || '',
    state: personal?.state || '', country: personal?.country || '', countryCode: personal?.country_code || '',
    contactNo: personal?.contact_no || '', nationality: personal?.nationality || '',
  });
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosClient.put(`/admin/users/${userId}/kyc-personal`, f);
      toast.success('Personal details saved.');
      onChanged();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save personal details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className={styles.editGrid}>
        <label className={styles.fld}>Title<Req />
          <select required value={f.title} onChange={(e) => set('title', e.target.value)}>
            <option value="">-- Select Title --</option>
            <option>Mr</option><option>Mrs</option><option>Ms</option><option>Dr</option>
          </select>
        </label>
        <label className={styles.fld}>First Name<Req />
          <input required placeholder="Enter first name" value={f.firstName} onChange={(e) => set('firstName', e.target.value)} />
        </label>
        <label className={styles.fld}>Middle Name
          <input placeholder="Enter middle name (optional)" value={f.middleName} onChange={(e) => set('middleName', e.target.value)} />
        </label>
        <label className={styles.fld}>Last Name<Req />
          <input required placeholder="Enter last name" value={f.lastName} onChange={(e) => set('lastName', e.target.value)} />
        </label>
        <label className={styles.fld}>Date of Birth<Req />
          <input required type="date" value={f.dob} onChange={(e) => set('dob', e.target.value)} />
        </label>
        <label className={styles.fld}>Gender<Req />
          <select required value={f.gender} onChange={(e) => set('gender', e.target.value)}>
            <option value="">-- Select Gender --</option>
            <option>Male</option><option>Female</option><option>Other</option>
          </select>
        </label>
        <label className={styles.fld}>Status<Req />
          <select required value={f.maritalStatus} onChange={(e) => set('maritalStatus', e.target.value)}>
            <option value="">-- Select Status --</option>
            <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
          </select>
        </label>
        <label className={styles.fld}>Email<Req />
          <input required type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
        </label>
        <label className={styles.fld}>Address<Req />
          <input required placeholder="Enter address" value={f.address} onChange={(e) => set('address', e.target.value)} />
        </label>
        <label className={styles.fld}>Country<Req />
          <select required value={f.country} onChange={(e) => setF((p) => ({ ...p, country: e.target.value, state: '' }))}>
            <option value="">-- Select Country --</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className={styles.fld}>State<Req />
          {f.country === 'India' ? (
            <select required value={f.state} onChange={(e) => set('state', e.target.value)}>
              <option value="">-- Select State --</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <input required placeholder="Enter state / province" value={f.state} onChange={(e) => set('state', e.target.value)} />
          )}
        </label>
        <label className={styles.fld}>City<Req />
          <input required placeholder="Enter city" value={f.city} onChange={(e) => set('city', e.target.value)} />
        </label>
        <label className={styles.fld}>Phone Country Code<Req />
          <select required value={f.countryCode} onChange={(e) => set('countryCode', e.target.value)}>
            <option value="">-- Select Country Code --</option>
            <option value="+1">+1 (USA)</option>
            <option value="+91">+91 (India)</option>
            <option value="+44">+44 (UK)</option>
            <option value="+61">+61 (Australia)</option>
          </select>
        </label>
        <label className={styles.fld}>Contact No<Req />
          <input required placeholder="Enter contact number" value={f.contactNo} onChange={(e) => set('contactNo', e.target.value)} />
        </label>
        <label className={styles.fld}>Nationality<Req />
          <input required placeholder="Enter nationality" value={f.nationality} onChange={(e) => set('nationality', e.target.value)} />
        </label>
      </div>
      <div className={styles.editActions}>
        <button type="submit" className={styles.saveBtn} disabled={saving}>{saving ? 'Saving…' : 'Save for user'}</button>
        <button type="button" className={styles.cancelEditBtn} onClick={onClose} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
};

/* ---------------- KYC documents ---------------- */

interface KycProps {
  userId: string;
  documents: any[];
  personal: any | null;
  Preview: PreviewComponent;
  onChanged: () => void;
}

export const AdminKycDocuments: React.FC<KycProps> = ({ userId, documents, personal, Preview, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState<DocCategory | ''>('');
  const [docType, setDocType] = useState('');
  const [fullName, setFullName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [dob, setDob] = useState('');
  const [front, setFront] = useState<File | null>(null);
  const [back, setBack] = useState<File | null>(null);

  const openForm = () => {
    // Same convenience as the user's form: pre-fill name and DOB from the saved personal details.
    const composed = personal ? [personal.first_name, personal.middle_name, personal.last_name].filter(Boolean).join(' ') : '';
    setFullName(composed);
    setDob(personal?.dob ? String(personal.dob).slice(0, 10) : '');
    setOpen(true);
  };

  const reset = () => {
    setOpen(false); setCategory(''); setDocType(''); setFullName(''); setIdNumber(''); setDob('');
    setFront(null); setBack(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !docType) { toast.error('Please select a document type.'); return; }
    if (category === 'identity' && (!front || !back)) { toast.error('Please upload both the front and back of the ID.'); return; }
    if (category !== 'identity' && !front) { toast.error('Please upload the document.'); return; }

    const form = new FormData();
    form.append('fullName', fullName);
    form.append('idNumber', idNumber);
    form.append('dob', dob);
    form.append('documentCategory', category);
    form.append('documentType', docType);
    if (category === 'identity') {
      if (front) form.append('front', front);
      if (back) form.append('back', back);
    } else if (front) {
      form.append('document', front);
    }
    setSaving(true);
    try {
      await axiosClient.post(`/admin/users/${userId}/kyc-documents`, form);
      toast.success('KYC document saved — pending approval.');
      reset();
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save the document.');
    } finally {
      setSaving(false);
    }
  };

  const existingForCategory = category ? documents.find((d) => d.document_category === category) : null;

  return (
    <div>
      {documents.length === 0 && <div className={styles.emptyText}>No KYC documents yet.</div>}
      {documents.map((d) => (
        <div key={d.id} className={styles.docBlock}>
          <div className={styles.docHead}>
            <b>{CATEGORY_LABEL[d.document_category as DocCategory] || d.document_category} - {d.document_type}</b>
            <span className={styles.docStatus} data-status={d.status}>{d.status}</span>
          </div>
          <div className={styles.listSub}>
            {d.full_name || '-'} {d.id_number ? `· ${d.id_number}` : ''} · {dateLabel(d.created_at)}
          </div>
          <div style={{ margin: '6px 0', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <FilledByBadge adminName={d.filled_by_admin_name} adminId={d.filled_by_admin_id} at={d.filled_by_admin_at} />
            <ReviewButtons url={`/admin/kyc/documents/${d.id}/status`} status={d.status} onChanged={onChanged} what="KYC document" />
          </div>
          <div className={styles.fileRow}>
            <Preview path={d.front_file_path} label={d.document_category === 'identity' ? 'Front' : 'Document'} />
            <Preview path={d.back_file_path} label="Back" />
          </div>
        </div>
      ))}

      {!open && (
        <button className={styles.editLink} style={{ marginTop: 10 }} onClick={openForm}>
          + Fill / replace a document for this user
        </button>
      )}
      {open && (
        <form onSubmit={submit} style={{ marginTop: 12 }}>
          <div className={styles.editGrid}>
            <label className={styles.fld}>Full Name<Req />
              <input required placeholder="Enter your full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </label>
            <label className={styles.fld}>ID Number<Req />
              <input required placeholder="Enter your ID number" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
            </label>
            <label className={styles.fld}>Date of Birth<Req />
              <input required type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </label>
            <label className={styles.fld}>Document Type<Req />
              <select
                required
                value={category}
                onChange={(e) => { setCategory(e.target.value as DocCategory); setDocType(''); setFront(null); setBack(null); }}
              >
                <option value="">Select Document Type</option>
                <option value="identity">Proof of Identity</option>
                <option value="address">Proof of Address</option>
                <option value="income">Proof of Income</option>
              </select>
            </label>
            {category && (
              <label className={styles.fld}>Select Document<Req />
                <select required value={docType} onChange={(e) => setDocType(e.target.value)}>
                  <option value="">Select Specific Document</option>
                  {DOC_OPTIONS[category].map((d) => <option key={d}>{d}</option>)}
                </select>
              </label>
            )}
            {category && docType && category === 'identity' && (
              <>
                <label className={styles.fileLabel}>Front Side of ID<Req />
                  <input type="file" accept={FILE_ACCEPT} onChange={(e) => setFront(e.target.files?.[0] || null)} />
                </label>
                <label className={styles.fileLabel}>Back Side of ID<Req />
                  <input type="file" accept={FILE_ACCEPT} onChange={(e) => setBack(e.target.files?.[0] || null)} />
                </label>
              </>
            )}
            {category && docType && category !== 'identity' && (
              <label className={styles.fileLabel}>Upload Document<Req />
                <input type="file" accept={FILE_ACCEPT} onChange={(e) => setFront(e.target.files?.[0] || null)} />
              </label>
            )}
          </div>
          {existingForCategory && (
            <div className={styles.listSub} style={{ marginBottom: 8 }}>
              A {existingForCategory.status} {CATEGORY_LABEL[category as DocCategory]} already exists — saving will replace it.
            </div>
          )}
          <div className={styles.editActions}>
            <button type="submit" className={styles.saveBtn} disabled={saving}>{saving ? 'Saving…' : 'Save for user'}</button>
            <button type="button" className={styles.cancelEditBtn} onClick={reset} disabled={saving}>Cancel</button>
          </div>
          <div className={styles.listSub} style={{ marginTop: 8 }}>
            Saved as "pending" and marked "Filled by admin". Use Approve to verify it; once verified the user cannot edit this section.
          </div>
        </form>
      )}
    </div>
  );
};

/* ---------------- KYB ---------------- */

interface KybProfileProps {
  userId: string;
  profile: any | null;
  onClose: () => void;
  onChanged: () => void;
}

export const AdminKybProfileForm: React.FC<KybProfileProps> = ({ userId, profile, onClose, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [p, setP] = useState({
    companyName: profile?.company_name || '', registrationNumber: profile?.registration_number || '',
    gstPan: profile?.gst_pan || '', companyAddress: profile?.company_address || '',
    incorporationCountry: profile?.incorporation_country || '', uboName: profile?.ubo_name || '',
    uboDesignation: profile?.ubo_designation || '', uboOwnershipPct: profile?.ubo_ownership_pct ?? '',
  });
  const set = (k: string, v: string) => setP((x) => ({ ...x, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosClient.put(`/admin/users/${userId}/kyb-profile`, p);
      toast.success('Company details saved — pending approval.');
      onChanged();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save company details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className={styles.editGrid}>
        <label className={styles.fld}>Company Name<Req />
          <input required placeholder="Enter registered company name" value={p.companyName} onChange={(e) => set('companyName', e.target.value)} />
        </label>
        <label className={styles.fld}>Registration Number<Req />
          <input required placeholder="Company registration / CIN number" value={p.registrationNumber} onChange={(e) => set('registrationNumber', e.target.value)} />
        </label>
        <label className={styles.fld}>GST / PAN of Company<Req />
          <input required placeholder="Enter GST or PAN" value={p.gstPan} onChange={(e) => set('gstPan', e.target.value)} />
        </label>
        <label className={styles.fld}>Incorporation Country<Req />
          <input required placeholder="Country of incorporation" value={p.incorporationCountry} onChange={(e) => set('incorporationCountry', e.target.value)} />
        </label>
        <label className={styles.fld} style={{ gridColumn: '1 / -1' }}>Company Address<Req />
          <input required placeholder="Registered office address" value={p.companyAddress} onChange={(e) => set('companyAddress', e.target.value)} />
        </label>
        <label className={styles.fld}>UBO Name<Req />
          <input required placeholder="Ultimate Beneficial Owner's name" value={p.uboName} onChange={(e) => set('uboName', e.target.value)} />
        </label>
        <label className={styles.fld}>UBO Designation<Req />
          <input required placeholder="e.g. Managing Partner, Director" value={p.uboDesignation} onChange={(e) => set('uboDesignation', e.target.value)} />
        </label>
        <label className={styles.fld}>UBO Ownership %<Req />
          <input required type="number" min={0} max={100} placeholder="e.g. 25" value={p.uboOwnershipPct} onChange={(e) => set('uboOwnershipPct', e.target.value)} />
        </label>
      </div>
      <div className={styles.editActions}>
        <button type="submit" className={styles.saveBtn} disabled={saving}>{saving ? 'Saving…' : 'Save for user'}</button>
        <button type="button" className={styles.cancelEditBtn} onClick={onClose} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
};

export const KybProfileReview: React.FC<{ profile: any; onChanged: () => void }> = ({ profile, onChanged }) => (
  <div style={{ margin: '6px 0 10px' }}>
    <ReviewButtons url={`/admin/kyb/profile/${profile.id}/status`} status={profile.status} onChanged={onChanged} what="KYB company profile" />
    <div className={styles.listSub} style={{ marginTop: 6 }}>
      The company profile can be approved once all three KYB documents are approved.
    </div>
  </div>
);

interface KybProps {
  userId: string;
  documents: any[];
  Preview: PreviewComponent;
  onChanged: () => void;
}

export const AdminKybDocuments: React.FC<KybProps> = ({ userId, documents, Preview, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const reset = () => { setOpen(false); setType(''); setFile(null); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { toast.error('Choose the document file.'); return; }
    const form = new FormData();
    form.append('documentType', type);
    form.append('document', file);
    setSaving(true);
    try {
      await axiosClient.post(`/admin/users/${userId}/kyb-documents`, form);
      toast.success('KYB document saved — pending approval.');
      reset();
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save the document.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      {documents.map((d) => (
        <div key={d.id} className={styles.docBlock}>
          <div className={styles.docHead}>
            <b>{KYB_DOCS.find((k) => k.key === d.document_type)?.label || d.document_type}</b>
            <span className={styles.docStatus} data-status={d.status}>{d.status}</span>
          </div>
          <div style={{ margin: '6px 0', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <FilledByBadge adminName={d.filled_by_admin_name} adminId={d.filled_by_admin_id} at={d.filled_by_admin_at} />
            <ReviewButtons url={`/admin/kyb/documents/${d.id}/status`} status={d.status} onChanged={onChanged} what="KYB document" />
          </div>
          <div className={styles.fileRow}>
            <Preview path={d.file_path} label={d.document_type} />
          </div>
        </div>
      ))}
      {!open && (
        <button className={styles.editLink} onClick={() => setOpen(true)}>+ Fill / replace a KYB document</button>
      )}
      {open && (
        <form onSubmit={submit}>
          <div className={styles.editGrid}>
            <label className={styles.fld}>Document<Req />
              <select required value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Select document</option>
                {KYB_DOCS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label className={styles.fileLabel}>Upload (PDF, JPG or PNG)<Req />
              <input type="file" accept={FILE_ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className={styles.editActions}>
            <button type="submit" className={styles.saveBtn} disabled={saving}>{saving ? 'Saving…' : 'Save for user'}</button>
            <button type="button" className={styles.cancelEditBtn} onClick={reset} disabled={saving}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};